import { motion } from "framer-motion";
import Navbar from "@/components/home/Navbar";
import Footer from "@/components/home/Footer";

const services = [
    {
        n: "01",
        titre: "Examen de vue",
        accent: "soigné",
        image: "/images/service-examen.jpg",
        description:
            "On prend le temps d'évaluer votre vision avec précision, dans un cadre confortable. Verres simples, progressifs, fatigue visuelle, lumière bleue : on en discute calmement pour trouver ce qui vous correspond.",
        details: [
            "Sans rendez-vous le matin",
            "Bilan visuel complet",
            "Conseils personnalisés",
        ],
    },
    {
        n: "02",
        titre: "Montage de lunettes",
        accent: "sur mesure",
        image: "/images/service-montage.jpg",
        description:
            "Du choix de la monture au montage des verres, tout se passe en atelier chez nous. Mesures précises, finitions soignées, ajustement parfait : vous repartez avec des lunettes qui vous vont vraiment.",
        details: [
            "Atelier sur place",
            "Mesures personnalisées",
            "Montage dans notre atelier",
        ],
    },
    {
        n: "03",
        titre: "Traitements & filtres",
        accent: "haut de gamme",
        image: "/images/service-traitements.jpg",
        description:
            "Anti-reflets, anti-lumière bleue, photochromiques, polarisants, teintes solaires, traitements oléophobes. On vous explique ce qui change vraiment au quotidien, sans jargon ni sur-vente.",
        details: [
            "Verres Zeiss",
            "Anti-fatigue numérique",
            "Solaires & polarisants",
        ],
    },
    {
        n: "04",
        titre: "Entretien & ajustement",
        accent: "à vie",
        image: "/images/service-entretien.jpg",
        description:
            "Une vis qui se desserre, une plaquette à changer, une branche qui frotte ? Passez quand vous voulez, on s'en occupe avec le sourire, gratuitement pour nos clients.",
        details: [
            "Ajustage offert",
            "Petites réparations sur place",
            "Nettoyage offert",
        ],
    },
    {
        n: "05",
        titre: "Conseil personnalisé",
        accent: "aux petits soins",
        image: "/images/service-conseil.jpg",
        description:
            "Le moment qu'on préfère. On essaie ensemble, on rit, on hésite, on compare, puis on réessaie. Jusqu'à trouver la monture qui vous ressemble vraiment. Pas une simple tendance, mais celle dans laquelle vous vous reconnaissez.",
        details: [
            "Essayages illimités",
            "Avis sincère, jamais forcé",
            "Suivi dans le temps",
        ],
    },
];

function ServiceRow({ s, index }) {
    const reverse = index % 2 === 1;
    const accentColors = [
        "#A3CFBB",
        "#DD7259",
        "#F2D580",
        "#3D6E5B",
        "#ECB6FF",
    ];
    const offsetColor = accentColors[index % accentColors.length];

    return (
        <motion.article
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center"
            data-testid={`service-row-${index}`}
        >
            {/* Image side */}
            <div
                className={`lg:col-span-6 relative ${
                    reverse ? "lg:order-2" : "lg:order-1"
                }`}
            >
                <div className="relative w-full max-w-md mx-auto">
                    <div
                        aria-hidden
                        className={`absolute -bottom-5 ${
                            reverse ? "-left-5" : "-right-5"
                        } w-full h-full`}
                        style={{ background: offsetColor }}
                    />
                    <img
                        src={s.image}
                        alt={s.titre}
                        className="relative block w-full aspect-[4/5] object-cover shadow-xl"
                        loading="lazy"
                        data-testid={`service-image-${index}`}
                    />
                </div>
            </div>

            {/* Text side */}
            <div
                className={`lg:col-span-6 ${
                    reverse ? "lg:order-1" : "lg:order-2"
                }`}
            >
                <p
                    className="font-serif italic text-2xl md:text-3xl mb-3"
                    style={{ color: offsetColor }}
                >
                    Service N°{s.n}
                </p>
                <h2
                    className="font-display font-semibold text-5xl md:text-6xl text-lpl-text leading-[0.95] tracking-tight mb-6"
                    data-testid={`service-title-${index}`}
                >
                    {s.titre}{" "}
                    <span className="font-serif italic font-normal text-lpl-primary-deep">
                        {s.accent}
                    </span>
                    <span className="font-serif italic font-normal text-lpl-lavender">
                        .
                    </span>
                </h2>
                <p className="text-lg md:text-xl text-lpl-text-muted leading-relaxed mb-8 max-w-lg">
                    {s.description}
                </p>
                <ul className="space-y-3">
                    {s.details.map((d, i) => (
                        <li
                            key={d}
                            className="flex items-start gap-3 font-display text-base md:text-lg text-lpl-text"
                        >
                            <span
                                aria-hidden
                                className="font-serif italic text-lpl-primary-deep text-2xl leading-none mt-0.5"
                            >
                                ↳
                            </span>
                            <span>{d}</span>
                            {i === s.details.length - 1 && null}
                        </li>
                    ))}
                </ul>
            </div>
        </motion.article>
    );
}

export default function Services() {
    return (
        <div
            className="min-h-screen font-body bg-lpl-bg text-lpl-text"
            data-testid="services-page"
        >
            <Navbar />

            <main>
                {/* Hero */}
                <section
                    className="relative pt-24 md:pt-28 pb-12 bg-lpl-bg-alt overflow-hidden"
                    data-testid="services-hero"
                >
                    <div
                        aria-hidden
                        className="pointer-events-none absolute -top-24 -right-20 w-[480px] h-[480px] rounded-full bg-lpl-lavender/30 hidden md:block"
                    />
                    <div className="relative max-w-7xl mx-auto px-5 md:px-10 py-16">
                        <motion.p
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="font-serif italic text-2xl md:text-3xl text-lpl-terracotta mb-4"
                        >
                            Ce qu'on fait pour vous
                        </motion.p>
                        <motion.h1
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: 0.1 }}
                            className="font-display font-semibold text-6xl md:text-8xl text-lpl-text leading-[0.9] tracking-tight max-w-5xl"
                            data-testid="services-title"
                        >
                            Nos{" "}
                            <span className="font-serif italic font-normal text-lpl-primary-deep">
                                services
                            </span>
                            <span className="font-serif italic font-normal text-lpl-lavender">
                                .
                            </span>
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.6, delay: 0.4 }}
                            className="mt-8 max-w-2xl text-lg md:text-xl text-lpl-text-muted leading-relaxed"
                        >
                            De l'examen de vue jusqu'au petit coup de tournevis
                            sur une branche un peu lâche, on s'occupe de tout.
                            Tranquillement, à votre rythme, et toujours avec
                            beaucoup de soin.
                        </motion.p>
                    </div>
                </section>

                {/* Services list */}
                <section
                    className="relative py-20 md:py-28 bg-lpl-bg"
                    data-testid="services-list"
                >
                    <div className="max-w-7xl mx-auto px-5 md:px-10 space-y-24 md:space-y-32">
                        {services.map((s, i) => (
                            <ServiceRow key={s.n} s={s} index={i} />
                        ))}
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
